import { jsx as _jsx } from "react/jsx-runtime";
import { Suspense, useEffect } from 'react';
import { loadable } from 'react-lazily/loadable';
import { thingViewMap } from '../ArbitrationObjects/defaultThingViewMap';
import { getControlsMap } from '../ArbitrationObjects/ListControlArbitrator/controlMap';
import { generateDomainTreeMapping } from '../components/navigation/DefaultNavMain/generateDomainTreeMapping';
import config from '../config';
import { AlertErrors } from '../ErrorHandling/ErrorBoundaryFallback';
import { changeAppState, setThingTypesMap } from '../redux/actions/AppActionFunctions/AppActions';
import { useMainDispatch } from '../redux/reducers/hooks';
import { RouterApex } from '../router/RouterApex';
import { ventureImports } from '../venture/index';
const debug = process.env.REACT_APP_DEBUG && false;
const { REACT_APP_PROJECT_CODE: projectCode } = process.env;
/** DEV ONLY, specify a venture code on start up with export VENTURE_CODE_OVERRIDE to use different venture ThingViewMap  */
const ventureCodeOverride = process.env.REACT_APP_VENTURE_CODE_OVERRIDE ?? null;
const resolvedVentureCode = ventureCodeOverride?.length ? ventureCodeOverride : projectCode?.substring(0, 4);
const AppContainer = loadable(() => import(/* webpackPreload: true */ `../venture/${projectCode?.substring(0, 4)}/AppContainer`).catch((e) => {
    debug && console.log(`AppContext err in getting AppContainer: ${e.message}`);
    return import(/* webpackPreload: true */ `./AppContainer`);
})).default;
export const AppContext = ({}) => {
    debug && console.log(`AppContext rerendered`);
    const dispatch = useMainDispatch();
    const getThingViewMap = async () => {
        debug && console.log(`AppContext getting ThingViewMap from ${resolvedVentureCode}`);
        // const module = await import(/* webpackPreload: true  */ `../venture/${resolvedVentureCode}/index`).catch((e) => {
        //   debug && console.log(`AppContext err getting ThingViewMap: ${e.message}`)
        //   return undefined
        // })
        const module = ventureImports;
        debug && console.log('AppContext has ThingViewMap module', module);
        const mergedThingViewMap = { ...thingViewMap, ...module?.thingViewMap, Group: { ...thingViewMap.Group, ...module?.thingViewMap?.Group } };
        debug && console.log('AppContext dispatching ThingViewMap', mergedThingViewMap);
        dispatch(setThingTypesMap(mergedThingViewMap));
    };
    const getExternalTreeDefinitionMap = async () => {
        debug && console.log(`AppContext getting ExternalTreeDefinitionMap from ${resolvedVentureCode}`);
        const module = await import(/* webpackPreload: true  */ `../venture/${resolvedVentureCode}/navigation/trees`).catch((e) => {
            debug && console.log(`AppContext err getting ExternalTreeDefinitionMap: ${e.message}`);
            return undefined;
        });
        debug && console.log('AppContext has ExternalTreeDefinitionMap module', module);
        const externalTreeDefinitionMap = module?.externalTreeDefinitionMap ?? null;
        debug && console.log('AppContext dispatching ExternalTreeDefinitionMap', externalTreeDefinitionMap);
        dispatch(changeAppState({ externalTreeDefinitionMap }));
    };
    const getExternalMenuOrdering = async () => {
        debug && console.log(`AppContext getting ExternalMenuOrdering from ${resolvedVentureCode}`);
        const module = await import(/* webpackPreload: true  */ `../venture/${resolvedVentureCode}/navigation/menuOrdering`).catch((e) => {
            debug && console.log(`AppContext err getting ExternalMenuOrdering: ${e.message}`);
            return undefined;
        });
        debug && console.log('AppContext has ExternalMenuOrdering module', module);
        const externalMenuOrdering = module?.externalMenuOrdering ?? null;
        debug && console.log('AppContext dispatching ExternalMenuOrdering', externalMenuOrdering);
        dispatch(changeAppState({ externalMenuOrdering }));
    };
    const getDomainTreeMapping = async () => {
        debug && console.log(`AppContext getting DomainTreeMapping from ${resolvedVentureCode} config`);
        const configDomainTreeMap = config.app.webUI.domainMap;
        let configDomainTreeMapping = null;
        if (configDomainTreeMap) {
            debug && console.log(`AppContext generating DomainTreeMapping from ${resolvedVentureCode} config`);
            configDomainTreeMapping = generateDomainTreeMapping(configDomainTreeMap);
            debug && console.log(`AppContext dispatching DomainTreeMapping from ${resolvedVentureCode} config`);
            dispatch(changeAppState({ domainTreeMapping: configDomainTreeMapping }));
        }
        debug && console.log(`AppContext getting DomainTreeMapping from ${resolvedVentureCode}`);
        const module = await import(/* webpackPreload: true  */ `../venture/${resolvedVentureCode}/navigation/domainTreeDefs`).catch((e) => {
            debug && console.log(`AppContext err getting DomainTreeMapping: ${e.message}`);
            return undefined;
        });
        debug && console.log('AppContext has DomainTreeMapping module', module);
        const domainTreeMapping = module?.domainTreeMapping ?? null;
        // If we generated a domain tree mapping from the config, and didn't find one from the venture-specific UI folder, early return
        if (configDomainTreeMapping !== null && domainTreeMapping === null)
            return undefined;
        debug && console.log('AppContext dispatching DomainTreeMapping', domainTreeMapping);
        dispatch(changeAppState({ domainTreeMapping }));
    };
    const dispatchControlsMap = async () => {
        dispatch(changeAppState({ controlsMap: getControlsMap() }));
    };
    useEffect(() => {
        getThingViewMap();
        dispatchControlsMap();
        getDomainTreeMapping();
        getExternalTreeDefinitionMap();
        getExternalMenuOrdering();
    }, []);
    debug && console.log('AppContext says projectCode is', projectCode?.substring(0, 4));
    return (_jsx(AlertErrors, { children: _jsx(Suspense, { children: _jsx(RouterApex, { App: AppContainer }) }) }));
};
